<template>
  <form id="supporter-details" class="card w-modal h-modal">
    <div class="header">
      <div class="title">
        <div class="flex flex-col">
          <h1 class="mb-2">New {{ addDonation ? 'Donation' : 'Supporter' }}</h1>
          <h4>Supporter Details</h4>
        </div>
      </div>
      <div class="close" @click="close()">
        <i class="icn icn-x sm"></i>
      </div>
    </div>
    <div v-if="isValidating" class="content">
      <div class="flex flex-col justify-center items-center h-full">
        <img class="h-36" src="@/assets/img/loading.svg" />
        <h2>Loading</h2>
      </div>
    </div>
    <div v-if="!isValidating" class="content">
      <div class="field">
        <h2>Supporter Details</h2>
        <div class="row mt-2">
          <div class="w-1/4">
            <select required v-model="payload.supporter.title">
              <option value="Mr">Mr</option>
              <option value="Mrs">Mrs</option>
              <option value="Ms">Ms</option>
              <option value="Miss">Miss</option>
              <option value="Dr">Dr</option>
              <option value="Mx">Mx</option>
            </select>
          </div>
        </div>
        <div class="row mt-2">
          <div class="field">
            <input
              required
              v-model="payload.supporter.firstName"
              type="text"
              placeholder="First name"
              autocomplete="nope"
              maxlength="127"
            />
          </div>
          <div class="field">
            <input
              required
              v-model="payload.supporter.lastName"
              type="text"
              placeholder="Last name"
              autocomplete="nope"
              maxlength="127"
            />
          </div>
        </div>
        <div class="row mt-2">
          <div class="field">
            <input
              id="phone-number"
              v-model="payload.supporter.phoneNumber"
              type="text"
              pattern="^(\(?\+?[0-9]*\)?)?[0-9_\- \(\)]*$"
              title="E.g: +61452609946 | 0452609946"
              placeholder="Phone (optional)"
              autocomplete="nope"
              @keydown="removeValidity('#phone-number')"
              maxlength="255"
            />
          </div>
        </div>
        <div class="row mt-2">
          <div class="field">
            <input
              id="email"
              v-model="payload.supporter.email"
              type="text"
              pattern="^[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}$"
              title="E.g: johndoe@example.com"
              placeholder="Email (optional)"
              autocomplete="nope"
              @keydown="removeValidity('#email')"
              maxlength="255"
            />
          </div>
        </div>
        <div v-if="payload.supporter.email">
          <div class="row mt-2">
            <div class="field">
              <label>
                <input v-model="payload.donation.isOptingIn" type="checkbox" />
                Send me emails about important conservation news and how I can help WWF protect the diversity of life on
                Earth
              </label>
            </div>
          </div>
          <div class="row mt-2">
            <div class="field">
              <label>
                <input v-model="payload.donation.contactForBequest" type="checkbox" />
                Send information in the Will
              </label>
            </div>
          </div>
        </div>
        <div class="row mt-2">
          <div class="field">
            <label>
              <input
                type="checkbox"
                @change="payload.donation.businessName = null"
                v-model="payload.donation.isOnBehalfOfBusiness"
              />
              Donate on behalf of a business
            </label>
          </div>
        </div>
        <div class="row mt-2">
          <div class="field">
            <input
              required
              type="text"
              placeholder="Business name"
              v-if="payload.donation.isOnBehalfOfBusiness"
              v-model="payload.donation.businessName"
              maxlength="127"
            />
          </div>
        </div>
        <div class="row mt-2">
          <div class="field">
            <select required v-model="payload.billingAddress.label">
              <option :value="l" v-for="l of labels" :key="l">
                {{ l }}
              </option>
            </select>
          </div>
        </div>
        <div class="row mt-2">
          <country-selector
            :country="payload.billingAddress.country"
            @countrySelected="clearAddress($event)"
          ></country-selector>
        </div>
        <div class="row mt-2" v-if="payload.billingAddress.country === australia">
          <autocomplete-address
            ref="autocomplete-address"
            :key="supporterFullAddressKey"
            @addressSelected="getSupporterSelectedAddress"
          ></autocomplete-address>
        </div>
        <div class="row mt-2">
          <div class="field">
            <input
              @change="updateAddress()"
              v-model="payload.billingAddress.unitNumber"
              type="text"
              placeholder="Unit number"
              autocomplete="nope"
              maxlength="31"
            />
          </div>
          <div class="field">
            <input
              required
              @change="updateAddress()"
              v-model="payload.billingAddress.streetNumber"
              type="text"
              placeholder="Street number"
              autocomplete="nope"
              maxlength="31"
            />
          </div>
        </div>
        <div class="row mt-2">
          <div class="field">
            <input
              required
              @change="updateAddress()"
              v-model="payload.billingAddress.streetName"
              type="text"
              placeholder="Street name"
              autocomplete="nope"
              maxlength="127"
            />
          </div>
          <div class="field">
            <input
              required
              @change="updateAddress()"
              v-model="payload.billingAddress.suburb"
              type="text"
              placeholder="Suburb"
              autocomplete="nope"
              maxlength="63"
            />
          </div>
        </div>
        <div class="row mt-2">
          <div class="field">
            <input
              required
              @change="updateAddress()"
              v-model="payload.billingAddress.postalCode"
              type="text"
              placeholder="Postcode"
              autocomplete="nope"
              maxlength="15"
            />
          </div>
          <div class="field">
            <select
              required
              @change="updateAddress()"
              name="state"
              v-if="payload.billingAddress.country === australia"
              v-model="payload.billingAddress.state"
            >
              <option value selected>Select a state</option>
              <option value="NSW">NSW</option>
              <option value="ACT">ACT</option>
              <option value="NT">NT</option>
              <option value="SA">SA</option>
              <option value="WA">WA</option>
              <option value="VIC">VIC</option>
              <option value="QLD">QLD</option>
              <option value="TAS">TAS</option>
            </select>
            <input
              v-if="payload.billingAddress.country !== australia"
              v-model="payload.billingAddress.state"
              type="text"
              placeholder="State"
              autocomplete="nope"
              maxlength="63"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="footer" v-if="!isValidating">
      <button class="secondary" type="button" @click="close()">Cancel</button>
      <button class="secondary" type="button" v-if="!addDonation" @click="reset()">Reset</button>
      <div class="flex-grow"></div>
      <button type="button" v-if="!addDonation" @click="saveSupporter()">Save</button>
      <button type="button" @click="goToNextStep()">Continue to donation</button>
    </div>
  </form>
</template>

<script>
import AutocompleteAddress from '@/components/AutocompleteAddress.vue';
import CountrySelector from '@/components/CountrySelector.vue';
import { ADDRESS_LABEL, CONTACT_TYPE } from '@/helpers/constants';
import { mapState } from 'vuex';

export default {
  name: 'SupporterDetails',
  data() {
    return {
      australia: 'Australia',
      supporterFullAddressKey: 0,
      isValidating: false,
    };
  },
  components: {
    AutocompleteAddress,
    CountrySelector,
  },
  computed: {
    ...mapState({
      payload: (state) => state.donationModal.payload,
    }),
    addDonation() {
      return this.$store.getters['donationModal/addDonation'];
    },
    labels() {
      return Object.values(ADDRESS_LABEL['ADDRESS']);
    },
  },
  methods: {
    close() {
      this.$store.dispatch('donationModal/close');
      this.$store.dispatch('donationModal/reset');
    },
    reset() {
      this.$store.dispatch('donationModal/reset');
      this.$refs['autocomplete-address'].reset();
    },
    async saveSupporter() {
      try {
        this.isValidating = true;
        this.payload.supporter.phoneNumber = this.payload.supporter.phoneNumber
          ? this.payload.supporter.phoneNumber.trim()
          : null;
        this.payload.supporter.email = this.payload.supporter.email ? this.payload.supporter.email.trim() : null;
        const isValid = document.querySelector('#supporter-details').reportValidity();
        if (isValid) {
          const isValidEmail = await this.isValidEmail();
          if (!isValidEmail) {
            setTimeout(() => {
              document.querySelector('#email').setCustomValidity('Invalid email');
              document.querySelector('#email').reportValidity();
            }, 100);
          } else {
            // Keep a copy of isOptingIn, contactForBequest, businessName in supporter model
            // This is the logic from Pandata, not a duplicate
            this.payload.supporter.isOptingIn = this.payload.donation.isOptingIn;
            this.payload.supporter.contactForBequest = this.payload.donation.contactForBequest;
            this.payload.supporter.businessName = this.payload.donation.businessName;

            // Check if supporter already exists
            // this.$store.dispatch('supporters/matchSupporter', {
            //   firstName: this.payload.supporter.firstName,
            //   lastName: this.payload.supporter.lastName,
            //   email: this.payload.supporter.email,
            // });

            // Transform to title case
            this.payload.supporter.firstName = this.toTitleCase(this.payload.supporter.firstName);
            this.payload.supporter.lastName = this.toTitleCase(this.payload.supporter.lastName);
            this.$store.dispatch('supporters/create', {
              ...this.payload.supporter,
              addresses: [this.payload.billingAddress],
              contacts: [
                { type: 'email', isPrimary: true, contact: this.payload.supporter.email },
                { type: 'phone', isPrimary: true, contact: this.payload.supporter.phoneNumber },
              ],
            });
            this.close();
          }
        }
      } catch (err) {
        console.log(err);
      } finally {
        this.isValidating = false;
      }
    },
    async goToNextStep() {
      try {
        this.isValidating = true;
        this.payload.supporter.phoneNumber = this.payload.supporter.phoneNumber
          ? this.payload.supporter.phoneNumber.trim()
          : null;
        this.payload.supporter.email = this.payload.supporter.email ? this.payload.supporter.email.trim() : null;
        const isValid = document.querySelector('#supporter-details').reportValidity();
        if (isValid) {
          const isValidEmail = await this.isValidEmail();
          if (!isValidEmail) {
            setTimeout(() => {
              document.querySelector('#email').setCustomValidity('Invalid email');
              document.querySelector('#email').reportValidity();
            }, 100);
          } else {
            // Keep a copy of isOptingIn, contactForBequest, businessName in supporter model
            // This is the logic from Pandata, not a duplicate
            this.payload.supporter.isOptingIn = this.payload.donation.isOptingIn;
            this.payload.supporter.contactForBequest = this.payload.donation.contactForBequest;
            this.payload.supporter.businessName = this.payload.donation.businessName;

            this.$store.dispatch('donationModal/updateAddress');
            this.$emit('nextStep');

            // Transform to title case
            this.payload.supporter.firstName = this.toTitleCase(this.payload.supporter.firstName);
            this.payload.supporter.lastName = this.toTitleCase(this.payload.supporter.lastName);
          }
        }
      } catch (err) {
        console.log(err);
      } finally {
        this.isValidating = false;
      }
    },
    isValidEmail() {
      if (this.payload.supporter.email) {
        return new Promise((resolve, reject) => {
          this.$store
            .dispatch('kickbox/validateEmail', this.payload.supporter.email)
            .then((validation) => {
              console.log(validation);
              return resolve(validation.valid);
            })
            .catch((err) => {
              return reject(err);
            });
        });
      } else {
        return true;
      }
    },
    removeValidity(id) {
      document.querySelector(id).setCustomValidity('');
    },
    getSupporterSelectedAddress({ address, fullLine }) {
      const temp = this.payload.billingAddress.label;
      this.payload.billingAddress = address;
      this.payload.billingAddress.label = temp;
      this.payload.billingAddress.unitNumber =
        address.unitNumber && address.unitNumber.trim() !== '' ? address.unitNumber.trim() : null;
      this.payload.addressFullLine = `${fullLine}, ${this.payload.billingAddress.country}`;
    },
    clearAddress(event) {
      document.querySelector('#phone-number').setCustomValidity('');
      this.payload.billingAddress.country = event.country;
      this.payload.countryCode = event.countryCode;
      this.$store.dispatch('donationModal/clearAddress');
    },
    updateAddress() {
      this.$store.dispatch('donationModal/updateAddress');
      // Reset full address input
      this.supporterFullAddressKey += 1;
    },
    toTitleCase(text) {
      const words = text.trim().toLowerCase().split(' ');
      for (let i = 0; i < words.length; i++) {
        words[i] = words[i][0].toUpperCase() + words[i].slice(1);
      }
      return words.join(' ');
    },
    fillInDetails(supporter) {
      this.payload.supporter = supporter;

      // Set up the two opt-in fields
      this.payload.donation.isOptingIn = supporter.isOptingIn;
      this.payload.donation.contactForBequest = supporter.contactForBequest;

      // Fill in address
      this.payload.billingAddress = supporter.addresses.sort((addr) => (addr.isPrimary ? -1 : 1))[0];

      // Fill in email
      this.payload.supporter.email = supporter.contacts
        .filter((contact) => contact.type === CONTACT_TYPE.EMAIL)
        .sort((contact) => (contact.isPrimary ? -1 : 1))[0]?.contact;

      // Fill in phone number
      this.payload.supporter.phoneNumber = supporter.contacts
        .filter((contact) => contact.type === CONTACT_TYPE.PHONE)
        .sort((contact) => (contact.isPrimary ? -1 : 1))[0]?.contact;
      console.log(supporter);
    },
  },
};
</script>

<style>
.dollar-handle {
  @apply min-w-1/5 max-w-1/5;
}
</style>
